/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 */

import axios from 'axios';

export const HTTP_ADAPTER = {
  FETCH: 'fetch',
  AXIOS: 'axios'
};

/**
 * True when the failure was an intentional abort (fetch signal, axios cancelToken/signal).
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRequestAbortError(error) {
  if (!error || typeof error !== 'object') return false;
  if (error.name === 'AbortError') return true;
  if (error.code === 'ERR_CANCELED') return true;
  if (typeof axios.isCancel === 'function' && axios.isCancel(error)) return true;
  return false;
}

/**
 * Thin wrapper around fetch or axios with the same `init` destructuring pattern for both.
 * @param {'fetch'|'axios'} adapter
 * @param {{ axiosInstance?: import('axios').AxiosInstance }} [factoryOptions]
 */
export function createHttpClient(adapter, factoryOptions = {}) {
  if (adapter === HTTP_ADAPTER.FETCH) {
    return {
      /** POST via fetch; `init` matches RequestInit (headers, body, signal, …). */
      post(url, init = {}) {
        const { headers, body, signal, ...rest } = init;
        return fetch(url, {
          method: 'POST',
          headers,
          body,
          signal,
          ...rest
        });
      }
    };
  }

  if (adapter === HTTP_ADAPTER.AXIOS) {
    const client = factoryOptions.axiosInstance || axios;
    return {
      /** POST via axios; `init` includes body (payload) plus headers, signal, cancelToken, … */
      post(url, init = {}) {
        const { headers, body, signal, cancelToken, ...rest } = init;
        return client.post(url, body, {
          headers,
          signal,
          cancelToken,
          ...rest
        });
      },
      /** GET via axios; `body` on `init` is stripped and not sent. */
      get(url, init = {}) {
        const { headers, body: _body, signal, cancelToken, ...rest } = init;
        return client.get(url, {
          headers,
          signal,
          cancelToken,
          ...rest
        });
      }
    };
  }

  throw new Error(`createHttpClient: unsupported adapter "${adapter}"`);
}
